import { useState } from 'react';
import SectionCard from '@/components/base/SectionCard';
import Badge from '@/components/base/Badge';
import { brandPresets } from '@/mocks/branding';
import { readableTextOn, normalizeHex } from '@/utils/branding';
import type { BrandColour, BrandPreset, BrandRole } from '@/types/portal';

interface BrandColourCardProps {
  colours: Record<BrandRole, string>;
  palette: BrandColour[];
  canEdit: boolean;
  onColour: (role: BrandRole, hex: string) => void;
  onPreset: (preset: BrandPreset) => void;
}

/** Candidate swatches for a role, gathered from the ready-made presets. */
function optionsFor(role: BrandRole, current: string): string[] {
  const set = new Set<string>([current]);
  brandPresets.forEach((preset) => set.add(preset[role]));
  return Array.from(set);
}

export default function BrandColourCard({
  colours,
  palette,
  canEdit,
  onColour,
  onPreset,
}: BrandColourCardProps) {
  const [openRole, setOpenRole] = useState<BrandRole | null>(null);
  const [hexDraft, setHexDraft] = useState('');
  const [hexError, setHexError] = useState('');

  const activePreset = brandPresets.find(
    (preset) =>
      preset.primary === colours.primary &&
      preset.accent === colours.accent &&
      preset.secondary === colours.secondary,
  );

  const toggleRole = (role: BrandRole, current: string) => {
    setOpenRole((prev) => (prev === role ? null : role));
    setHexDraft(current.toUpperCase());
    setHexError('');
  };

  const commitHex = (role: BrandRole) => {
    const normalised = normalizeHex(hexDraft);
    if (!normalised) {
      setHexError('Enter a valid hex — e.g. #FF6200 or #F60');
      return;
    }
    onColour(role, normalised);
    setHexDraft(normalised);
    setHexError('');
  };

  const pickFromWheel = (role: BrandRole, value: string) => {
    const upper = value.toUpperCase();
    onColour(role, upper);
    setHexDraft(upper);
    setHexError('');
  };

  return (
    <SectionCard
      title="Brand colours"
      subtitle="Three roles drive every button, badge and highlight in the portal"
      action={
        <Badge tone={activePreset ? 'accent' : 'neutral'} icon="ri-palette-line">
          {activePreset ? activePreset.name : 'Custom palette'}
        </Badge>
      }
    >
      <div className="space-y-3">
        {palette.map((entry) => {
          const hex = colours[entry.role];
          const open = openRole === entry.role;
          const options = optionsFor(entry.role, hex);
          return (
            <div
              key={entry.role}
              className="rounded-md border border-background-200 bg-background-100 p-3"
            >
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  disabled={!canEdit}
                  onClick={() => toggleRole(entry.role, hex)}
                  aria-label={`Change ${entry.label} colour`}
                  className="relative h-10 w-10 shrink-0 rounded-md border border-background-300 cursor-pointer disabled:cursor-not-allowed"
                  style={{ backgroundColor: hex }}
                >
                  <span
                    className="absolute inset-0 flex items-center justify-center text-base"
                    style={{ color: readableTextOn(hex) }}
                  >
                    <i className="ri-palette-line" />
                  </span>
                </button>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-foreground-900">{entry.label}</p>
                  <p className="text-xs text-foreground-500">{entry.usage}</p>
                </div>
                <span className="rounded border border-background-300 bg-background-50 px-2 py-1 text-[11px] font-label text-foreground-600">
                  {hex.toUpperCase()}
                </span>
              </div>

              {open && canEdit ? (
                <div className="mt-3 space-y-3 border-t border-background-200 pt-3">
                  <div className="flex flex-wrap items-center gap-2">
                    {options.map((option) => {
                      const selected = option.toLowerCase() === hex.toLowerCase();
                      return (
                        <button
                          key={option}
                          type="button"
                          onClick={() => pickFromWheel(entry.role, option)}
                          aria-label={`Use ${option}`}
                          className={`flex h-7 w-7 items-center justify-center rounded-full border cursor-pointer transition-transform hover:scale-105 ${
                            selected ? 'ring-2 ring-foreground-400 ring-offset-1' : 'border-background-300'
                          }`}
                          style={{ backgroundColor: option }}
                        >
                          {selected ? (
                            <span
                              className="text-xs"
                              style={{ color: readableTextOn(option) }}
                            >
                              <i className="ri-check-line" />
                            </span>
                          ) : null}
                        </button>
                      );
                    })}
                  </div>

                  <div className="flex flex-wrap items-center gap-2 rounded-md border border-dashed border-background-300 bg-background-50 p-2">
                    <label
                      htmlFor={`hex-wheel-${entry.role}`}
                      className="sr-only"
                    >
                      Colour wheel for {entry.label}
                    </label>
                    <input
                      id={`hex-wheel-${entry.role}`}
                      type="color"
                      value={hex}
                      onChange={(event) => pickFromWheel(entry.role, event.target.value)}
                      className="h-8 w-8 shrink-0 cursor-pointer rounded-md border border-background-300 bg-background-50 p-0.5"
                      aria-label={`Colour wheel for ${entry.label}`}
                    />
                    <div className="relative flex-1 min-w-[140px]">
                      <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-foreground-400">
                        <i className="ri-hashtag" />
                      </span>
                      <input
                        type="text"
                        value={hexDraft.replace(/^#/, '')}
                        spellCheck={false}
                        maxLength={6}
                        placeholder="FF6200"
                        onChange={(event) => {
                          setHexDraft(`#${event.target.value.replace(/[^0-9a-fA-F]/g, '')}`);
                          setHexError('');
                        }}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') commitHex(entry.role);
                        }}
                        className="h-8 w-full rounded-md border border-background-300 bg-background-50 pl-7 pr-2 text-xs font-label uppercase text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                        aria-label={`Hex value for ${entry.label}`}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => commitHex(entry.role)}
                      className="h-8 shrink-0 rounded-md border border-primary-300 bg-primary-50 px-3 text-xs font-medium text-primary-700 cursor-pointer hover:bg-primary-100"
                    >
                      Apply
                    </button>
                  </div>

                  {hexError ? (
                    <p className="inline-flex items-center gap-1 text-[11px] text-primary-700">
                      <i className="ri-error-warning-line" />
                      {hexError}
                    </p>
                  ) : (
                    <p className="text-[11px] text-foreground-500">
                      Type any hex, use the wheel, or pick a swatch above.
                    </p>
                  )}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>

      <div className="mt-5 border-t border-background-100 pt-4">
        <div className="flex items-center justify-between">
          <p className="text-xs font-label font-medium text-foreground-700">Ready-made palettes</p>
          <span className="text-[11px] text-foreground-500">Applies all three roles</span>
        </div>
        <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
          {brandPresets.map((preset) => {
            const selected = activePreset?.id === preset.id;
            return (
              <button
                key={preset.id}
                type="button"
                disabled={!canEdit}
                onClick={() => onPreset(preset)}
                className={`flex items-center justify-between gap-2 rounded-md border px-3 py-2 text-left transition-colors cursor-pointer disabled:cursor-not-allowed ${
                  selected
                    ? 'border-primary-400 bg-primary-50'
                    : 'border-background-300 bg-background-50 hover:bg-background-100'
                }`}
              >
                <span className="min-w-0">
                  <span className="flex items-center gap-1.5 text-xs font-medium text-foreground-900">
                    <span className="truncate">{preset.name}</span>
                    {preset.tag ? (
                      <span
                        className={`shrink-0 rounded-full px-1.5 py-px text-[10px] font-semibold ${
                          preset.tag === 'Main' ? 'bg-primary-100 text-primary-800' : 'bg-background-200 text-foreground-700'
                        }`}
                      >
                        {preset.tag}
                      </span>
                    ) : null}
                  </span>
                  <span className="mt-1 flex gap-1">
                    <span
                      className="h-3 w-3 rounded-full border border-background-300"
                      style={{ backgroundColor: preset.primary }}
                    />
                    <span
                      className="h-3 w-3 rounded-full border border-background-300"
                      style={{ backgroundColor: preset.accent }}
                    />
                    <span
                      className="h-3 w-3 rounded-full border border-background-300"
                      style={{ backgroundColor: preset.secondary }}
                    />
                  </span>
                </span>
                {selected ? (
                  <i className="ri-checkbox-circle-fill text-base text-primary-600" />
                ) : (
                  <i className="ri-circle-line text-base text-foreground-300" />
                )}
              </button>
            );
          })}
        </div>
      </div>
    </SectionCard>
  );
}